import React, { useState, useEffect, useMemo } from "react";
import ProjectList from "./ProjectList";
import TaskList from "./TaskList";
import InlineTaskForm from "./InlineTaskForm";
import DataExport from "./DataExport";
import TimerActivityLog from "./TimerActivityLog";
import db from "../utils/db";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../utils/helpers";

const TaskPanel = ({ sessionRefreshTrigger }) => {
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterProject, setFilterProject] = useState("all");
  const [isTimerActivityExpanded, setIsTimerActivityExpanded] = useState(false);

  useEffect(() => {
    initializeData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const initializeData = async () => {
    try {
      await db.init();
      const projectsData = await db.getProjects();
      const tasksData = await db.getTasks();

      setProjects(Array.isArray(projectsData) ? projectsData : []);
      setTasks(Array.isArray(tasksData) ? tasksData : []);

      // Set first project as selected if none selected
      if (
        Array.isArray(projectsData) &&
        projectsData.length > 0 &&
        !selectedProject
      ) {
        setSelectedProject(projectsData[0]);
      }
    } catch (error) {
      console.error("Failed to initialize data:", error);
      showErrorNotification("Failed to load data. Please refresh the page.");
      setProjects([]);
      setTasks([]);
    }
  };

  const handleAddProject = async (projectData) => {
    try {
      const newProject = await db.addProject(projectData);
      setProjects([...projects, newProject]);
      setSelectedProject(newProject);
      showSuccessNotification("Project created successfully!");
    } catch (error) {
      console.error("Failed to add project:", error);
      showErrorNotification("Failed to create project. Please try again.");
    }
  };

  const handleUpdateProject = async (projectData) => {
    try {
      await db.updateProject(projectData);
      setProjects(
        projects.map((p) => (p.id === projectData.id ? projectData : p))
      );

      // Update selected project if it was the one being updated
      if (selectedProject && selectedProject.id === projectData.id) {
        setSelectedProject(projectData);
      }

      showSuccessNotification("Project updated successfully!");
    } catch (error) {
      console.error("Failed to update project:", error);
      showErrorNotification("Failed to update project. Please try again.");
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      await db.deleteProject(projectId);
      setProjects(projects.filter((p) => p.id !== projectId));

      // Update tasks to remove deleted project's tasks
      setTasks(tasks.filter((t) => t.projectId !== projectId));

      // Select another project if current was deleted
      if (selectedProject && selectedProject.id === projectId) {
        const remainingProjects = projects.filter((p) => p.id !== projectId);
        setSelectedProject(
          remainingProjects.length > 0 ? remainingProjects[0] : null
        );
      }
    } catch (error) {
      console.error("Failed to delete project:", error);
    }
  };

  const handleAddTask = async (taskData) => {
    try {
      const newTask = await db.addTask({
        ...taskData,
        projectId: selectedProject?.id || null,
      });
      setTasks([...tasks, newTask]);
      setShowTaskForm(false);
      showSuccessNotification("Task created successfully!");
      return newTask; // Return the new task so InlineTaskForm can get the ID
    } catch (error) {
      console.error("Failed to add task:", error);
      showErrorNotification("Failed to create task. Please try again.");
      throw error; // Re-throw so InlineTaskForm can handle the error
    }
  };

  const handleUpdateTask = async (taskData) => {
    try {
      await db.updateTask(taskData);
      setTasks(tasks.map((t) => (t.id === taskData.id ? taskData : t)));
      setShowTaskForm(false);
      setEditingTask(null);

      // If task status changed and not viewing "all", switch filter to match the new status
      if (taskData.status !== filterStatus && filterStatus !== "all") {
        setFilterStatus(taskData.status);
        showSuccessNotification(
          `Task updated! Filter switched to ${
            taskData.status === "completed" ? "Completed" : "To Do"
          } tasks.`
        );
      } else {
        showSuccessNotification("Task updated successfully!");
      }
      return taskData; // Return the updated task
    } catch (error) {
      console.error("Failed to update task:", error);
      showErrorNotification("Failed to update task. Please try again.");
      throw error; // Re-throw so InlineTaskForm can handle the error
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await db.deleteTask(taskId);
      setTasks(tasks.filter((t) => t.id !== taskId));
    } catch (error) {
      console.error("Failed to delete task:", error);
    }
  };

  // Memoize filtered tasks to avoid recalculating on every render
  const filteredTasks = useMemo(() => {
    if (!Array.isArray(tasks)) return [];

    return tasks.filter((task) => {
      // Filter by project
      if (filterProject !== "all" && task.projectId !== filterProject)
        return false;

      // Filter by status
      if (filterStatus !== "all" && task.status !== filterStatus) return false;

      return true;
    });
  }, [tasks, filterProject, filterStatus]);

  return (
    <div className="task-panel">
      {/* Scrollable Task Management Content */}
      <div className="task-management-content">
        <div className="task-panel-header">
          <h2>Task Management</h2>
          <DataExport />
        </div>

        <ProjectList
          projects={projects}
          selectedProject={selectedProject}
          onSelectProject={setSelectedProject}
          onAddProject={handleAddProject}
          onUpdateProject={handleUpdateProject}
          onDeleteProject={handleDeleteProject}
        />

        <div className="task-controls">
          <div className="filter-controls">
            <div className="filter-group">
              <label>Project:</label>
              <select
                value={filterProject}
                onChange={(e) => setFilterProject(e.target.value)}
                className="project-filter"
              >
                <option value="all">All Projects</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Task Status:</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="status-filter"
              >
                <option value="all">All Tasks</option>
                <option value="todo">To Do</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>

          <button
            className="add-task-btn"
            onClick={() => setShowTaskForm(true)}
          >
            + Add Task
          </button>
        </div>

        {showTaskForm && (
          <InlineTaskForm
            project={selectedProject}
            editingTask={editingTask}
            onSave={editingTask ? handleUpdateTask : handleAddTask}
            onCancel={() => {
              setShowTaskForm(false);
              setEditingTask(null);
            }}
            projects={projects}
          />
        )}

        <TaskList
          tasks={filteredTasks}
          projects={projects}
          onDeleteTask={handleDeleteTask}
          onUpdateTask={handleUpdateTask}
        />
      </div>

      {/* Fixed Timer Activity Log at Bottom */}
      <div
        className={`timer-activity-log-container ${
          isTimerActivityExpanded ? "expanded" : ""
        }`}
      >
        <button
          className="expand-toggle-btn-full"
          onClick={() => setIsTimerActivityExpanded(!isTimerActivityExpanded)}
          title={
            isTimerActivityExpanded
              ? "Collapse Timer Activity"
              : "Expand Timer Activity to full height"
          }
        >
          {isTimerActivityExpanded
            ? "− Collapse Timer Activity"
            : "+ Expand Timer Activity"}
        </button>

        <TimerActivityLog
          tasks={tasks}
          projects={projects}
          onUpdateSession={() => {
            // Refresh tasks to show updated time tracking
            initializeData();
          }}
          refreshTrigger={sessionRefreshTrigger}
          onExpandedChange={setIsTimerActivityExpanded}
          isExpanded={isTimerActivityExpanded}
        />
      </div>
    </div>
  );
};

export default TaskPanel;
